import { ColumnDef, SortingState } from "@tanstack/react-table";
import { Card, Table, toast } from "@win-reality/react-ui";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useDashboardStore } from "@/stores/dashboard";
import { getErrorDetail } from "@/utils/errorUtils";

import { useTeamTopPlayers } from "./hooks/useTeamTopPlayers";

type LeaderboardProps = {
  teamId: string;
};

type HitterRow = {
  id: string;
  rank: number;
  name: string;
  exitVelocity: number | null | undefined;
  distance: number | null | undefined;
  launchAngle: number | null | undefined;
};

type PitcherRow = {
  id: string;
  rank: number;
  name: string;
  velocity: number | null | undefined;
  inVertBreak: number | null | undefined;
  horizontalBreak: number | null | undefined;
};

export const Leaderboard = ({ teamId }: LeaderboardProps) => {
  const { includePractice, filter, selectedYear } = useDashboardStore();

  const [sortingState, setSortingState] = useState<SortingState>([]);

  const { hitters, pitchers, isLoadingTopPlayers, topPlayersError } =
    useTeamTopPlayers(
      teamId,
      filter,
      filter === "hitting" ? "exitVelocity" : "velocity",
      includePractice
        ? filter !== "hitting"
          ? "bullpen"
          : "batting_practice"
        : "game",
      selectedYear,
    );

  useEffect(() => {
    if (topPlayersError) {
      const errorMessage = getErrorDetail(topPlayersError);
      toast(`Error while loading leaderboard information: ${errorMessage}`, {
        variant: "error",
      });
    }
  }, [topPlayersError]);

  const hitterColumns = useMemo(
    (): ColumnDef<HitterRow>[] => [
      {
        header: "Rank/Name",
        accessorKey: "name",
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="text-text-light">{row.original.rank}</span>
            <Link
              to={`/players/${row.original.id}/home`}
              className="text-blue hover:underline"
            >
              {row.original.name}
            </Link>
          </div>
        ),
      },
      {
        header: "Exit Velocity",
        accessorKey: "exitVelocity",
        cell: ({ row }) => row.original.exitVelocity?.toFixed(2) ?? "-",
      },
      {
        header: "Proj. Distance",
        accessorKey: "distance",
        cell: ({ row }) => row.original.distance?.toFixed(2) ?? "-",
      },
      {
        header: "Launch Angle",
        accessorKey: "launchAngle",
        cell: ({ row }) => row.original.launchAngle?.toFixed(2) ?? "-",
      },
    ],
    [],
  );

  const pitcherColumns = useMemo(
    (): ColumnDef<PitcherRow>[] => [
      {
        header: "Rank/Name",
        accessorKey: "name",
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="text-text-light">{row.original.rank}</span>
            <Link
              to={`/players/${row.original.id}/home`}
              className="text-blue hover:underline"
            >
              {row.original.name}
            </Link>
          </div>
        ),
      },
      {
        header: "Velocity",
        accessorKey: "velocity",
        cell: ({ row }) => row.original.velocity?.toFixed(2) ?? "-",
      },
      {
        header: "In. Vert Break",
        accessorKey: "inVertBreak",
        cell: ({ row }) => row.original.inVertBreak?.toFixed(2) ?? "-",
      },
      {
        header: "Horizontal Break",
        accessorKey: "horizontalBreak",
        cell: ({ row }) => row.original.horizontalBreak?.toFixed(2) ?? "-",
      },
    ],
    [],
  );

  const hitterData = useMemo(
    (): HitterRow[] =>
      (hitters ?? []).map((h, index) => ({
        ...h,
        rank: index + 1,
      })),
    [hitters],
  );

  const pitcherData = useMemo(
    (): PitcherRow[] =>
      (pitchers ?? []).map((p, index) => ({
        ...p,
        rank: index + 1,
      })),
    [pitchers],
  );

  const renderContent = () => {
    if (topPlayersError) {
      return (
        <div className="w-full flex items-center justify-center p-10">
          <p className="text-sm text-accent-dark-3">
            Unable to load leaderboard data
          </p>
        </div>
      );
    }

    return filter === "hitting" ? (
      <Table
        size="compact"
        data={hitterData}
        columns={hitterColumns}
        total={hitterData.length}
        isFetching={isLoadingTopPlayers}
        sortingState={sortingState}
        onSortingChange={setSortingState}
        headerClassName="text-xs font-medium text-accent-dark-3 uppercase"
        cellClassName="text-sm text-text-light"
      />
    ) : (
      <Table
        size="compact"
        data={pitcherData}
        columns={pitcherColumns}
        total={pitcherData.length}
        isFetching={isLoadingTopPlayers}
        sortingState={sortingState}
        onSortingChange={setSortingState}
        headerClassName="text-xs font-medium text-accent-dark-3 uppercase"
        cellClassName="text-sm text-text-light"
      />
    );
  };

  return (
    <div className="relative flex flex-col">
      <h2 className="text-lg font-bold text-text-light mb-4">Leaderboard</h2>
      <Card className="bg-white light:bg-white rounded-md p-4">
        {renderContent()}
      </Card>
    </div>
  );
};
